import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles,
  Calendar,
  User,
  Mail,
  Phone,
  Home as HomeIcon,
  ArrowRight,
  Heart,
  Shield,
  MessageCircle,
  ClipboardList,
  Compass,
  Flame,
  CheckCircle2,
  Clock,
  Video,
} from "lucide-react";
import { toast } from "sonner";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { api } from "@/lib/api";
import { COACHING_OPTIONS, TRAINING_EXPERIENCE_OPTIONS } from "@/lib/content";
import SEO, { faqJsonLd } from "@/components/SEO";
import TestimonialsCarousel from "@/components/TestimonialsCarousel";

/* ---------- Static content ---------- */
const COACHING_ICONS = {
  "Free Discovery Call": Sparkles,
  "Online Strength Coaching": Calendar,
  "Postpartum Strength Coaching": Heart,
  "Beginner Gym Coaching": User,
  "In-Person PT Enquiry": User,
  "Home Visit Enquiry": HomeIcon,
};

const PROCESS_STEPS = [
  {
    step: "01",
    title: "Send your enquiry",
    body: "Fill in the form below — a few minutes of honest answers about where you are, what you'd love to feel and what's held you back so far.",
    icon: ClipboardList,
  },
  {
    step: "02",
    title: "Friendly reply within 24–48h",
    body: "I personally read every enquiry. You'll hear back from me — never a bot — with the coaching path I think suits you best.",
    icon: MessageCircle,
  },
  {
    step: "03",
    title: "Your free discovery call",
    body: "A relaxed 20-minute conversation to talk through your goals, lifestyle and any nerves. Zero pressure. Zero sales.",
    icon: Compass,
  },
  {
    step: "04",
    title: "Begin your strength journey",
    body: "If it feels right, we begin. Your first plan, your first session, your first deep breath — coaching that actually fits your life.",
    icon: Flame,
  },
];

const BENEFITS = [
  {
    title: "A safe, women-only space",
    body: "No mirrors-and-machines intimidation. Just a calm, supportive place to be heard and helped.",
    icon: "Shield",
  },
  {
    title: "Coaching shaped around your life",
    body: "Busy weeks, postpartum recovery, irregular sleep — your plan flexes around real life, not the other way round.",
    icon: "Heart",
  },
  {
    title: "Beginner-friendly, always",
    body: "If lifting weights still feels new, you're in exactly the right place. We start gently and build at your pace.",
    icon: "Sparkles",
  },
  {
    title: "Honest, no-pressure conversation",
    body: "Your consultation is genuinely free — and you'll never be pushed into a package that isn't right for you.",
    icon: "MessageCircle",
  },
];

const WHO_THIS_IS_FOR = [
  "Women new to strength training who want to learn properly",
  "Mums rebuilding strength after children (postpartum strength coaching)",
  "Women who feel nervous, lost or invisible in mainstream gyms",
  "Women returning to fitness after months — or years — away",
  "Women wanting online coaching that genuinely fits their schedule",
  "Women in London looking for a women-only personal trainer",
];

const BOOKING_TESTIMONIALS = [
  {
    quote: "I almost didn't message — I'm so glad I did.",
    body:
      "I'd been putting off contacting a coach for over a year because I felt too unfit, too unsure, too nervous. Maya replied so warmly that I cried a little. The discovery call felt like talking to a friend.",
    name: "Lauren",
    role: "Online Coaching client",
  },
  {
    quote: "Zero pressure — and exactly what I needed.",
    body:
      "I expected a sales pitch. Instead Maya asked about my life, my body, my goals, and helped me figure out what actually made sense for me right now. That honesty is rare.",
    name: "Priya",
    role: "Postpartum client",
  },
];

const FAQ_ITEMS = [
  {
    q: "Is the consultation really free?",
    a: "Yes — the discovery call is genuinely free, with no obligation to book anything afterwards. It's simply a chance to chat, share your goals and decide if working together feels right.",
  },
  {
    q: "What if I've never trained before?",
    a: "You're exactly who this is for. Beginner gym coaching for women is one of my favourite things to do — we start with the basics, no judgement, no embarrassment, no rush.",
  },
  {
    q: "I'm postpartum — is it safe to start?",
    a: "Yes, postpartum strength coaching is one of my specialisms. I work with mums from 6 weeks to many years postpartum, rebuilding gently around your body, your recovery and your motherhood.",
  },
  {
    q: "Do I need to be in London?",
    a: "Not at all. Online coaching for women is available wherever you are — UK or worldwide. In-person training is available if you're based in or visiting London.",
  },
  {
    q: "How long until you reply?",
    a: "I personally respond to every enquiry within 24–48 hours (Mon–Fri). You'll always hear back from me directly — never a bot or assistant.",
  },
  {
    q: "What happens after I submit?",
    a: "You'll receive a friendly email confirmation, and I'll be in touch shortly to arrange your free discovery call at a time that suits you.",
  },
];

const TRUST_STATS = [
  { stat: "500+", label: "Women coached" },
  { stat: "24–48h", label: "Personal reply" },
  { stat: "100%", label: "Women-only space" },
  { stat: "London", label: "& worldwide online" },
];

/* ---------- Helpers ---------- */
const ICONS = { Shield, Heart, Sparkles, MessageCircle };
function BenefitIcon({ name, size = 22 }) {
  const C = ICONS[name] || Heart;
  return <C size={size} strokeWidth={1.5} className="text-[#E8A6B8]" />;
}

/* ---------- Page ---------- */
export default function Booking() {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    coaching_option: "",
    main_goal: "",
    training_experience: "",
    injuries: "",
    preferred_times: "",
    message: "",
  });

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.coaching_option) {
      toast.error("Please choose a coaching option");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/bookings", form);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
      toast.success("Enquiry sent. Maya will be in touch soon.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit enquiry");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="booking-page" className="bg-[#F6EFE9]">
      <SEO
        title="Book a Consultation | Women's Strength Coach London — HerFitHub"
        description="Book a free, no-pressure consultation with Maya — women's strength coach in London. Online coaching for women, postpartum strength coaching and beginner gym coaching designed around real life."
        path="/booking"
        keywords="book women's strength coach London, women's personal trainer London consultation, online coaching for women, postpartum strength coaching, beginner gym coaching for women, women-only personal trainer London"
        jsonLd={[faqJsonLd(FAQ_ITEMS)]}
      />

      {submitted ? (
        <SuccessState name={form.name} option={form.coaching_option} />
      ) : (
        <>
          <Hero />
          <TrustStrip />
          <LimitedIntake />
          <CalendlyEmbed />
          <WhatToExpect />
          <Benefits />
          <FormSection
            form={form}
            update={update}
            submit={submit}
            setForm={setForm}
            submitting={submitting}
          />
          <WhoThisIsFor />
          <Testimonials />
          <FAQ />
          <FinalCTA />
        </>
      )}
    </div>
  );
}

/* ---------- HERO ---------- */
function Hero() {
  return (
    <section className="relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-16">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7 fade-in">
            <p className="eyebrow mb-6" data-testid="booking-eyebrow">
              Book Your Consultation · Free & No Pressure
            </p>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] text-[#3A3A3A]">
              Let's build your{" "}
              <span className="italic text-[#E8A6B8]">confidence</span>, together.
            </h1>
            <p className="heart-divider text-left mt-5">♡</p>
            <p className="mt-7 text-base sm:text-lg leading-relaxed text-[#5C5C5C] max-w-xl">
              Whether you're brand new to strength training, returning after
              children, or rebuilding confidence after a long time away — this
              is a safe, supportive place to start. One honest conversation, no
              pressure, no judgement.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="#book-call"
                className="btn-pink"
                data-testid="hero-book-cta"
              >
                Book Consultation <ArrowRight size={16} />
              </a>
              <a
                href="#what-to-expect"
                className="btn-outline"
                data-testid="hero-expect-cta"
              >
                What To Expect
              </a>
            </div>
            <p className="mt-8 text-[11px] sm:text-xs uppercase tracking-[0.16em] text-[#5C5C5C] flex flex-wrap items-center gap-x-3 gap-y-1.5">
              <span>Women-only space</span>
              <span className="text-[#E8A6B8]">•</span>
              <span>Beginner friendly</span>
              <span className="text-[#E8A6B8]">•</span>
              <span>London & online</span>
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="relative">
              <div
                aria-hidden
                className="absolute -top-5 -left-5 w-3/4 h-3/4 rounded-[2.5rem] bg-[#F3C9D3]/40 -z-0"
              />
              <div className="relative z-10 rounded-[2rem] overflow-hidden aspect-[4/5] bg-[#F3C9D3] shadow-[0_18px_44px_rgba(232,166,184,0.22)]">
                <img
                  src="https://images.pexels.com/photos/3768916/pexels-photo-3768916.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=720"
                  alt="Women's personal trainer London — consultation with Maya"
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover"
                  data-testid="booking-hero-image"
                />
                <div className="absolute bottom-5 left-5 right-5 sm:left-6 sm:right-6 bg-white/95 backdrop-blur-md rounded-2xl px-5 py-4">
                  <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#E8A6B8]">
                    Real conversations · Real care
                  </p>
                  <p className="font-serif-display italic text-base sm:text-lg text-[#3A3A3A] mt-1 leading-snug">
                    "You don't have to start strong. You just have to start."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-[#F3C9D3]/40 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-40 -left-32 w-[420px] h-[420px] rounded-full bg-[#E8A6B8]/15 blur-3xl"
      />
    </section>
  );
}

/* ---------- TRUST STRIP ---------- */
function TrustStrip() {
  return (
    <section aria-label="Trust signals" className="bg-white border-y border-[#F3C9D3]/40">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-8 grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
        {TRUST_STATS.map((t, i) => (
          <div key={t.label} data-testid={`trust-stat-${i}`}>
            <p className="font-serif-display text-3xl sm:text-4xl text-[#3A3A3A]">
              {t.stat}
            </p>
            <p className="mt-1 text-[10px] sm:text-xs uppercase tracking-[0.18em] text-[#5C5C5C]">
              {t.label}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------- WHAT TO EXPECT ---------- */
function WhatToExpect() {
  return (
    <section id="what-to-expect" className="py-20 lg:py-28 scroll-mt-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="eyebrow mb-3">The Coaching Process</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-[#3A3A3A]">
            What to <span className="italic text-[#E8A6B8]">expect</span>
          </h2>
          <p className="mt-5 text-[#5C5C5C]">
            From the moment you send your enquiry, every step is designed to
            feel calm, supportive and human. Here's exactly how it works.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
          {PROCESS_STEPS.map((s, i) => {
            const I = s.icon;
            return (
              <article
                key={s.step}
                className="relative bg-white rounded-[1.75rem] p-7 border border-[#F3C9D3]/40 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_44px_rgba(232,166,184,0.22)]"
                data-testid={`process-step-${i}`}
              >
                <span className="absolute top-6 right-6 font-serif-display text-3xl text-[#F3C9D3]">
                  {s.step}
                </span>
                <div className="inline-flex p-3 rounded-2xl bg-[#F3C9D3]/40 mb-5">
                  <I size={22} strokeWidth={1.5} className="text-[#E8A6B8]" />
                </div>
                <h3 className="font-serif-display text-xl text-[#3A3A3A] leading-snug">
                  {s.title}
                </h3>
                <p className="mt-3 text-sm text-[#5C5C5C] leading-relaxed">
                  {s.body}
                </p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------- BENEFITS ---------- */
function Benefits() {
  return (
    <section className="bg-white py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <p className="eyebrow mb-4">Why Book A Consultation</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#3A3A3A] leading-tight">
            A gentle, honest <br />
            <span className="italic text-[#E8A6B8]">first step</span>
          </h2>
          <p className="mt-7 text-base sm:text-lg leading-relaxed text-[#5C5C5C] max-w-md">
            Your consultation isn't a sales call — it's a real conversation about
            you, your body, your life and what would genuinely help you feel
            stronger and more at home in yourself.
          </p>
          <Link
            to="/services"
            className="btn-outline mt-9"
            data-testid="benefits-services-link"
          >
            Explore Coaching Options <ArrowRight size={14} />
          </Link>
        </div>
        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
          {BENEFITS.map((b, i) => (
            <article
              key={b.title}
              className="bg-[#F6EFE9] rounded-[1.75rem] p-6 border border-[#F3C9D3]/30 transition-all duration-300 hover:-translate-y-1 hover:bg-white hover:shadow-[0_18px_40px_rgba(232,166,184,0.2)]"
              data-testid={`benefit-${i}`}
            >
              <div className="inline-flex p-3 rounded-2xl bg-white border border-[#F3C9D3]/40 mb-4">
                <BenefitIcon name={b.icon} />
              </div>
              <h3 className="font-serif-display text-lg text-[#3A3A3A] leading-snug">
                {b.title}
              </h3>
              <p className="mt-2 text-sm text-[#5C5C5C] leading-relaxed">
                {b.body}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- LIMITED MONTHLY INTAKE ---------- */
function LimitedIntake() {
  return (
    <section
      aria-label="Limited monthly intake"
      className="px-6 lg:px-10 pb-2 -mt-4 lg:-mt-10"
    >
      <div className="max-w-5xl mx-auto">
        <article
          className="relative overflow-hidden rounded-[2rem] border border-[#F3C9D3]/50 bg-gradient-to-br from-white via-[#F6EFE9] to-[#F3C9D3]/40 shadow-[0_18px_44px_rgba(232,166,184,0.16)] px-7 sm:px-12 py-10 sm:py-12"
          data-testid="limited-intake-card"
        >
          {/* Soft decorative blush halo */}
          <div
            aria-hidden
            className="pointer-events-none absolute -top-20 -right-16 w-72 h-72 rounded-full bg-[#E8A6B8]/15 blur-3xl"
          />
          <div className="relative grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-2 flex lg:justify-center">
              <span className="inline-flex p-4 rounded-full bg-white border border-[#F3C9D3]/50 shadow-sm">
                <Sparkles size={22} strokeWidth={1.5} className="text-[#E8A6B8]" />
              </span>
            </div>
            <div className="lg:col-span-7">
              <p
                className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#E8A6B8]"
                data-testid="limited-intake-eyebrow"
              >
                A More Personal Coaching Experience
              </p>
              <h3 className="font-serif-display text-3xl sm:text-4xl text-[#3A3A3A] leading-tight mt-2">
                Coaching spaces are{" "}
                <span className="italic text-[#E8A6B8]">kept intentionally limited</span>
              </h3>
              <p className="mt-4 text-sm sm:text-base text-[#5C5C5C] leading-relaxed max-w-2xl">
                HerFitHub intentionally keeps women's strength coaching spaces
                limited each month — so every woman, whether starting beginner-friendly
                coaching or continuing online coaching for women, receives genuine
                support, attention and guidance throughout her journey.
              </p>
              <p className="mt-5 text-[11px] sm:text-xs uppercase tracking-[0.18em] text-[#5C5C5C] flex flex-wrap items-center gap-x-3 gap-y-1.5">
                <Heart size={12} className="text-[#E8A6B8]" strokeWidth={1.8} />
                <span>Limited monthly intake</span>
                <span className="text-[#E8A6B8]">•</span>
                <span>Women-only personal trainer in London &amp; online</span>
              </p>
            </div>
            <div className="lg:col-span-3 flex lg:justify-end">
              <a
                href="#book-call"
                className="btn-pink w-full lg:w-auto"
                data-testid="limited-intake-cta"
              >
                Save Your Spot <ArrowRight size={14} />
              </a>
            </div>
          </div>
        </article>
      </div>
    </section>
  );
}

/* ---------- CALENDLY EMBED ---------- */
const CALENDLY_URL =
  "https://calendly.com/herfithub/30min?hide_landing_page_details=1&hide_gdpr_banner=1&hide_event_type_details=0&primary_color=e8a6b8";
const CALENDLY_SCRIPT = "https://assets.calendly.com/assets/external/widget.js";

function CalendlyEmbed() {
  const sectionRef = useRef(null);
  const [loaded, setLoaded] = useState(false);

  // Lazy-load the Calendly script only when the section nears the viewport.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const el = sectionRef.current;
    if (!el) return undefined;

    const inject = () => {
      if (document.querySelector(`script[src="${CALENDLY_SCRIPT}"]`)) {
        setLoaded(true);
        return;
      }
      const s = document.createElement("script");
      s.src = CALENDLY_SCRIPT;
      s.async = true;
      s.onload = () => setLoaded(true);
      document.body.appendChild(s);
    };

    if (!("IntersectionObserver" in window)) {
      inject();
      return undefined;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            inject();
            io.disconnect();
          }
        });
      },
      { rootMargin: "200px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section
      id="book-call"
      ref={sectionRef}
      className="py-20 lg:py-28 scroll-mt-24 bg-[#F3C9D3]/25"
      aria-label="Book a consultation call with Maya"
    >
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <p className="eyebrow mb-3" data-testid="calendly-eyebrow">
            Book Your Free Consultation Call
          </p>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-[#3A3A3A]">
            Choose a <span className="italic text-[#E8A6B8]">time that suits you</span>
          </h2>
          <p className="mt-5 text-base text-[#5C5C5C] leading-relaxed">
            Book a relaxed consultation call where we can discuss your goals,
            training background, confidence and the type of support that would
            suit you best{" "}
            <span aria-hidden className="text-[#E8A6B8]">🤍</span>
          </p>
          <p className="mt-4 text-[11px] uppercase tracking-[0.18em] text-[#5C5C5C] inline-flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5">
            <Video size={12} className="text-[#E8A6B8]" strokeWidth={1.8} />
            <span>Google Meet link auto-generated</span>
            <span className="text-[#E8A6B8]">•</span>
            <span>30 minutes</span>
            <span className="text-[#E8A6B8]">•</span>
            <span>No pressure, ever</span>
          </p>
        </div>

        <div className="relative rounded-[2rem] overflow-hidden bg-white border border-[#F3C9D3]/40 shadow-[0_18px_44px_rgba(232,166,184,0.16)]">
          {!loaded && (
            <div
              className="absolute inset-0 flex items-center justify-center"
              data-testid="calendly-loading"
              aria-hidden
            >
              <div className="text-center">
                <div className="inline-flex p-4 rounded-full bg-[#F3C9D3]/40 mb-4">
                  <Calendar size={22} strokeWidth={1.5} className="text-[#E8A6B8] animate-pulse" />
                </div>
                <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#3A3A3A]">
                  Loading calendar…
                </p>
              </div>
            </div>
          )}
          <div
            className="calendly-inline-widget"
            data-url={CALENDLY_URL}
            data-testid="calendly-widget"
            style={{ minWidth: "320px", height: "1000px", position: "relative", zIndex: 1 }}
          />
        </div>

        <p className="mt-6 text-center text-xs text-[#5C5C5C]">
          Prefer to share more context first?{" "}
          <a
            href="#booking-form"
            className="text-[#E8A6B8] font-semibold hover:underline"
            data-testid="calendly-prefer-form-link"
          >
            Send a written enquiry instead
          </a>
          .
        </p>
      </div>
    </section>
  );
}

/* ---------- FORM SECTION ---------- */
function FormSection({ form, update, submit, setForm, submitting }) {
  return (
    <section id="booking-form" className="py-20 lg:py-28 scroll-mt-24 bg-[#F3C9D3]/30">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="eyebrow mb-3">Prefer To Write?</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-[#3A3A3A]">
            Send a <span className="italic text-[#E8A6B8]">written enquiry</span>
          </h2>
          <p className="mt-5 text-[#5C5C5C]">
            If you'd rather share your story in writing first, fill in the form
            below. Take your time. Maya will reply personally within 24–48 hours.
          </p>
        </div>
        <div className="grid lg:grid-cols-12 gap-10">
          {/* Coaching option chooser */}
          <aside className="lg:col-span-5">
            <div className="bg-white rounded-[2rem] p-8 border border-[#F3C9D3]/40 sticky top-24">
              <p className="eyebrow mb-5">Choose An Option</p>
              <ul className="space-y-3">
                {COACHING_OPTIONS.map((opt) => {
                  const Icon = COACHING_ICONS[opt] || Sparkles;
                  const active = form.coaching_option === opt;
                  return (
                    <li key={opt}>
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, coaching_option: opt })}
                        data-testid={`coaching-option-${opt
                          .toLowerCase()
                          .replace(/[\s/]+/g, "-")}`}
                        aria-pressed={active}
                        className={`w-full text-left flex items-center gap-3 px-5 py-4 rounded-2xl border transition-all duration-300 ${
                          active
                            ? "border-[#E8A6B8] bg-[#F3C9D3]/40 shadow-[0_8px_24px_rgba(232,166,184,0.2)]"
                            : "border-[#F3C9D3]/40 bg-white hover:border-[#E8A6B8]/60 hover:-translate-y-0.5"
                        }`}
                      >
                        <span
                          className={`p-2 rounded-full transition-colors ${
                            active
                              ? "bg-[#E8A6B8] text-white"
                              : "bg-[#F3C9D3]/60 text-[#3A3A3A]"
                          }`}
                        >
                          <Icon size={16} strokeWidth={1.6} />
                        </span>
                        <span className="text-sm font-medium text-[#3A3A3A]">
                          {opt}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
              <div className="mt-8 pt-6 border-t border-[#F3C9D3]/40 flex items-start gap-3">
                <Clock size={18} className="text-[#E8A6B8] mt-0.5" strokeWidth={1.6} />
                <p className="text-xs text-[#5C5C5C] leading-relaxed">
                  Maya replies personally within{" "}
                  <strong className="text-[#3A3A3A]">24–48 hours</strong> (Mon–Fri).
                  Every enquiry is read with care.
                </p>
              </div>
            </div>
          </aside>

          {/* Form */}
          <form
            onSubmit={submit}
            className="lg:col-span-7 bg-white rounded-[2rem] p-6 sm:p-10 border border-[#F3C9D3]/40 space-y-5 shadow-[0_18px_44px_rgba(232,166,184,0.12)]"
            data-testid="booking-form"
            noValidate
          >
            <Field label="Full Name" icon={User} required>
              <input
                type="text"
                required
                value={form.name}
                onChange={update("name")}
                className="hfh-input"
                data-testid="booking-name"
                autoComplete="name"
              />
            </Field>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Email Address" icon={Mail} required>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={update("email")}
                  className="hfh-input"
                  data-testid="booking-email"
                  autoComplete="email"
                />
              </Field>
              <Field label="Phone Number" icon={Phone} required>
                <input
                  type="tel"
                  required
                  value={form.phone}
                  onChange={update("phone")}
                  className="hfh-input"
                  data-testid="booking-phone"
                  autoComplete="tel"
                />
              </Field>
            </div>

            <Field label="Preferred Coaching Option" required>
              <select
                required
                value={form.coaching_option}
                onChange={update("coaching_option")}
                className="hfh-input"
                data-testid="booking-coaching-option"
              >
                <option value="">Select an option…</option>
                {COACHING_OPTIONS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="What is your main goal?" required>
              <input
                type="text"
                required
                placeholder="e.g. Build strength, rebuild after kids, feel confident…"
                value={form.main_goal}
                onChange={update("main_goal")}
                className="hfh-input"
                data-testid="booking-goal"
              />
            </Field>

            <Field label="Training Experience" required>
              <select
                required
                value={form.training_experience}
                onChange={update("training_experience")}
                className="hfh-input"
                data-testid="booking-experience"
              >
                <option value="">Select your level…</option>
                {TRAINING_EXPERIENCE_OPTIONS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Any injuries or medical considerations?">
              <input
                type="text"
                value={form.injuries}
                onChange={update("injuries")}
                placeholder="Optional — anything you'd like Maya to know"
                className="hfh-input"
                data-testid="booking-injuries"
              />
            </Field>

            <Field label="Preferred days / times">
              <input
                type="text"
                value={form.preferred_times}
                onChange={update("preferred_times")}
                placeholder="e.g. Weekday mornings"
                className="hfh-input"
                data-testid="booking-times"
              />
            </Field>

            <Field label="Anything else? (optional)">
              <textarea
                rows={4}
                value={form.message}
                onChange={update("message")}
                className="hfh-input resize-none"
                placeholder="Worries, questions, context — whatever feels useful to share."
                data-testid="booking-message"
              />
            </Field>

            <div className="pt-2">
              <button
                type="submit"
                disabled={submitting}
                className="btn-pink w-full disabled:opacity-60"
                data-testid="booking-submit"
              >
                {submitting ? "Sending…" : (
                  <>
                    Let's Build Your Confidence <ArrowRight size={16} />
                  </>
                )}
              </button>
              <p className="mt-4 text-center text-[11px] text-[#5C5C5C] leading-relaxed">
                Your details stay private and are only used to reply to your enquiry.
              </p>
            </div>
          </form>
        </div>
      </div>

      <style>{`
        .hfh-input {
          width: 100%;
          background: #FFFFFF;
          border: 1px solid #F3C9D3;
          border-radius: 0.85rem;
          padding: 0.85rem 1rem;
          font-size: 0.95rem;
          color: #3A3A3A;
          transition: border-color 200ms, box-shadow 200ms;
          font-family: inherit;
        }
        .hfh-input:focus {
          outline: none;
          border-color: #E8A6B8;
          box-shadow: 0 0 0 4px rgba(232,166,184,0.18);
        }
      `}</style>
    </section>
  );
}

function Field({ label, icon: Icon, required, children }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#3A3A3A] mb-2 inline-flex items-center gap-2">
        {Icon ? <Icon size={13} className="text-[#E8A6B8]" strokeWidth={1.8} /> : null}
        {label}
        {required ? <span className="text-[#E8A6B8]" aria-hidden>*</span> : null}
      </span>
      {children}
    </label>
  );
}

/* ---------- WHO THIS IS FOR ---------- */
function WhoThisIsFor() {
  return (
    <section className="py-20 lg:py-28 bg-white">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="eyebrow mb-3">Who This Is For</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-[#3A3A3A]">
            If even <span className="italic text-[#E8A6B8]">one</span> of these sounds
            like you…
          </h2>
          <p className="mt-5 text-[#5C5C5C]">
            You're in the right place. Sending an enquiry doesn't commit you to anything.
          </p>
        </div>
        <ul className="grid sm:grid-cols-2 gap-4 max-w-3xl mx-auto">
          {WHO_THIS_IS_FOR.map((line, i) => (
            <li
              key={line}
              className="flex items-start gap-3 bg-[#F6EFE9] rounded-2xl px-5 py-4 border border-[#F3C9D3]/30"
              data-testid={`who-line-${i}`}
            >
              <CheckCircle2
                size={18}
                className="text-[#E8A6B8] flex-shrink-0 mt-0.5"
                strokeWidth={1.6}
              />
              <span className="text-sm text-[#3A3A3A] leading-snug">{line}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ---------- TESTIMONIALS ---------- */
function Testimonials() {
  return (
    <section className="bg-[#F3C9D3]/30 py-20 lg:py-28">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="eyebrow mb-3">Words From The Women I Coach</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-[#3A3A3A]">
            You're in <span className="italic text-[#E8A6B8]">good company</span>
          </h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8 mb-12">
          {BOOKING_TESTIMONIALS.map((t, i) => (
            <article
              key={t.name}
              className="bg-white rounded-[2rem] p-8 border border-[#F3C9D3]/40 shadow-[0_14px_30px_rgba(232,166,184,0.12)]"
              data-testid={`booking-testimonial-${i}`}
            >
              <p className="font-serif-display italic text-2xl text-[#3A3A3A] leading-snug">
                "{t.quote}"
              </p>
              <p className="mt-4 text-sm text-[#5C5C5C] leading-relaxed">{t.body}</p>
              <p className="mt-6 text-[11px] uppercase tracking-[0.18em] font-semibold text-[#3A3A3A]">
                {t.name}
                <span className="text-[#E8A6B8] mx-2">•</span>
                <span className="text-[#5C5C5C]">{t.role}</span>
              </p>
            </article>
          ))}
        </div>
        <TestimonialsCarousel />
      </div>
    </section>
  );
}

/* ---------- FAQ ---------- */
function FAQ() {
  return (
    <section className="py-20 lg:py-28">
      <div className="max-w-3xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-12">
          <p className="eyebrow mb-3">FAQ</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-[#3A3A3A]">
            Before you send, <span className="italic text-[#E8A6B8]">a few answers</span>
          </h2>
        </div>
        <Accordion
          type="single"
          collapsible
          className="bg-white rounded-[2rem] border border-[#F3C9D3]/40 px-6 sm:px-8 divide-y divide-[#F3C9D3]/30"
        >
          {FAQ_ITEMS.map((item, i) => (
            <AccordionItem
              key={item.q}
              value={`item-${i}`}
              className="border-0"
              data-testid={`booking-faq-${i}`}
            >
              <AccordionTrigger className="text-left text-[#3A3A3A] font-medium text-base sm:text-lg py-6 hover:no-underline">
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-[#5C5C5C] text-sm sm:text-base leading-relaxed pb-6">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

/* ---------- FINAL CTA ---------- */
function FinalCTA() {
  return (
    <section className="px-6 lg:px-10 pb-24 pt-4">
      <div
        className="max-w-6xl mx-auto rounded-[2rem] overflow-hidden relative text-center"
        style={{
          background:
            "linear-gradient(135deg, #E8A6B8 0%, #F3C9D3 50%, #F6EFE9 100%)",
        }}
      >
        <div className="relative px-6 sm:px-10 py-20 sm:py-24">
          <p className="eyebrow text-[#3A3A3A]/80 mb-4">Ready When You Are</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#3A3A3A] leading-tight max-w-2xl mx-auto">
            Let's build your <span className="italic">confidence</span>
          </h2>
          <p className="mt-7 text-base sm:text-lg text-[#3A3A3A]/80 leading-relaxed max-w-xl mx-auto">
            One enquiry, one honest conversation, and a coach who's actually in
            your corner. That's how this starts.
          </p>
          <a
            href="#book-call"
            className="mt-10 inline-flex items-center justify-center gap-2 bg-[#3A3A3A] hover:bg-black text-white rounded-full px-10 py-4 text-[12px] uppercase tracking-[0.18em] font-semibold transition-all hover:-translate-y-0.5 shadow-xl"
            data-testid="booking-final-cta"
          >
            Start Your Journey <ArrowRight size={16} />
          </a>
          <p
            className="mt-6 text-[11px] uppercase tracking-[0.18em] text-[#3A3A3A]/70 inline-flex items-center justify-center gap-2"
            data-testid="final-cta-microcopy"
          >
            <Sparkles size={11} className="text-[#3A3A3A]/70" strokeWidth={1.8} />
            Limited monthly intake for a more personal coaching experience
          </p>
        </div>
      </div>
    </section>
  );
}

/* ---------- SUCCESS STATE ---------- */
function SuccessState({ name, option }) {
  const firstName = name?.split(" ")[0] || "there";
  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6 py-24" data-testid="booking-success">
      <div className="max-w-2xl text-center bg-white rounded-[2rem] p-10 sm:p-16 border border-[#F3C9D3]/40 shadow-[0_18px_44px_rgba(232,166,184,0.18)]">
        <div className="inline-flex p-4 rounded-full bg-[#F3C9D3]/40 mb-6">
          <Heart size={32} strokeWidth={1.5} className="text-[#E8A6B8]" />
        </div>
        <p className="eyebrow mb-3">Enquiry Received</p>
        <h1 className="font-serif-display text-4xl sm:text-5xl text-[#3A3A3A] leading-tight">
          Thank you,{" "}
          <span className="italic text-[#E8A6B8]">{firstName}</span>.
        </h1>
        <p className="heart-divider mt-4">♡</p>
        <p className="mt-6 text-[#5C5C5C] leading-relaxed">
          Your {option ? <strong className="text-[#3A3A3A]">{option}</strong> : "consultation"}{" "}
          enquiry has been received. Maya personally reads every message and
          will get back to you within <strong className="text-[#3A3A3A]">24–48 hours</strong>{" "}
          to talk through your goals and the best next step.
        </p>
        <p className="mt-6 text-sm text-[#5C5C5C] italic">
          In the meantime, take a deep breath — you've taken the hardest step already.
        </p>
        <div className="mt-10 flex flex-wrap justify-center gap-4">
          <Link to="/programs" className="btn-pink" data-testid="success-programs-link">
            Explore Programs <ArrowRight size={14} />
          </Link>
          <Link to="/blog" className="btn-outline" data-testid="success-blog-link">
            Read The Journal
          </Link>
        </div>
      </div>
    </div>
  );
}
